import { component } from "picoapp";

declare function removeTrapFocus(
  elementToFocus?: HTMLElement
): void;

function getVisibleFocusables(container: HTMLElement): HTMLElement[] {
  const selector =
    "a[href], button:not([disabled]), input:not([type=hidden]):enabled, select:enabled, textarea:enabled, [tabindex]:not([tabindex^='-'])";
  return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => el.offsetParent !== null
  );
}

/** After Tab on last focusable in a submenu: next main link, or first focusable after the main nav in header order. */
function focusNextTopLevelOrOut(
  header: HTMLElement,
  navUl: HTMLElement,
  currentTrigger: HTMLElement
): void {
  const topFocusables = Array.from(
    navUl.querySelectorAll<HTMLElement>(":scope > li > a, :scope > li > button")
  );
  const idx = topFocusables.indexOf(currentTrigger);
  if (idx >= 0 && idx < topFocusables.length - 1) {
    topFocusables[idx + 1].focus();
    return;
  }
  const ordered = getVisibleFocusables(header);
  const lastMain = topFocusables[topFocusables.length - 1];
  const i = ordered.indexOf(lastMain);
  if (i >= 0 && i < ordered.length - 1) {
    ordered[i + 1].focus();
  }
}

const SCROLL_LOCK_CLASS = "stop-scroll";

function lockPageScroll(): void {
  document.documentElement.classList.add(SCROLL_LOCK_CLASS);
}

function unlockPageScroll(): void {
  document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
}

function getChildListHandleFromPanel(el: HTMLElement): string | null {
  const prefix = "site-nav-dropdown__menu--";
  for (const c of el.classList) {
    if (!c.startsWith(prefix)) continue;
    const rest = c.slice(prefix.length);
    if (rest && rest !== "active") {
      return rest;
    }
  }
  return null;
}

const carouselInitialized = new WeakSet<HTMLElement>();

function initNavCarousels(scope: HTMLElement): void {
  scope.querySelectorAll<HTMLElement>("[data-nav-carousel]").forEach((carousel) => {
    const track = carousel.querySelector<HTMLElement>("[data-carousel-track]");
    const toolbar = carousel.querySelector<HTMLElement>(
      ".site-nav-dropdown__carousel-toolbar"
    );
    const prev = carousel.querySelector<HTMLButtonElement>(
      ".site-nav-dropdown__carousel-btn--prev"
    );
    const next = carousel.querySelector<HTMLButtonElement>(
      ".site-nav-dropdown__carousel-btn--next"
    );
    if (!track || !prev || !next) {
      return;
    }

    const scrollStep = (): number =>
      Math.max(200, Math.floor(track.clientWidth * 0.85));

    /** Hide arrow toolbar when all slides fit; subpixels often report 1–4px “overflow” with no real scroll. */
    const OVERFLOW_EPS_PX = 12;

    const updateCarousel = (): void => {
      const { scrollLeft, scrollWidth, clientWidth } = track;
      if (clientWidth < 1) {
        return;
      }
      const overflowPx = scrollWidth - clientWidth;
      const needsPagination = overflowPx > OVERFLOW_EPS_PX;
      if (toolbar) {
        toolbar.classList.toggle(
          "site-nav-dropdown__carousel-toolbar--no-scroll",
          !needsPagination
        );
        toolbar.setAttribute(
          "aria-hidden",
          needsPagination ? "false" : "true"
        );
      }
      if (!needsPagination) {
        prev.disabled = true;
        next.disabled = true;
        return;
      }
      prev.disabled = scrollLeft <= 2;
      next.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
    };

    const scheduleCarouselMeasure = (): void => {
      updateCarousel();
      requestAnimationFrame(() => {
        updateCarousel();
        requestAnimationFrame(updateCarousel);
      });
      [50, 150, 400, 800].forEach((ms) => {
        window.setTimeout(updateCarousel, ms);
      });
    };

    if (!carouselInitialized.has(carousel)) {
      carouselInitialized.add(carousel);
      prev.addEventListener("click", () => {
        track.scrollBy({ left: -scrollStep(), behavior: "smooth" });
      });
      next.addEventListener("click", () => {
        track.scrollBy({ left: scrollStep(), behavior: "smooth" });
      });
      track.addEventListener("scroll", updateCarousel, { passive: true });
      const ro = new ResizeObserver(updateCarousel);
      ro.observe(track);
      const productRow = track.querySelector<HTMLElement>(
        ".site-nav-dropdown__product-row"
      );
      if (productRow) {
        ro.observe(productRow);
      }
      track.querySelectorAll("img").forEach((img) => {
        const bump = (): void => {
          updateCarousel();
        };
        if (img.complete) {
          bump();
        } else {
          img.addEventListener("load", bump, { once: true });
        }
      });
      track.addEventListener("keydown", (e: KeyboardEvent) => {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          track.scrollBy({ left: -scrollStep(), behavior: "smooth" });
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          track.scrollBy({ left: scrollStep(), behavior: "smooth" });
        }
      });
    }

    scheduleCarouselMeasure();
  });
}

function queryPanelByHandle(
  scope: HTMLElement,
  handle: string | null
): HTMLElement | null {
  if (!handle) {
    return null;
  }
  return scope.querySelector<HTMLElement>(`#${CSS.escape(handle)}`);
}

function activateInnerPanel(
  clicked: HTMLElement,
  innerParents: NodeListOf<HTMLElement>,
  scope: HTMLElement
): void {
  innerParents.forEach((button) => {
    const handle = button.getAttribute("data-handle");
    const menu = queryPanelByHandle(scope, handle);
    const nested = button.firstElementChild as HTMLElement | null;

    if (button === clicked) {
      nested?.setAttribute("aria-expanded", "true");
      button.classList.add("site-nav-dropdown__child-link--active");
      menu?.classList.add("site-nav-dropdown__child-menu--active");
    } else {
      nested?.setAttribute("aria-expanded", "false");
      button.classList.remove("site-nav-dropdown__child-link--active");
      menu?.classList.remove("site-nav-dropdown__child-menu--active");
    }
  });
}

export const megaMenu = component((node: HTMLElement) => {
  const overlay = document.querySelector<HTMLElement>(".overlay");
  let lastActiveTrigger: HTMLElement | null = null;

  const openDropdown = (
    dropdownLink: HTMLElement,
    navDropdown: HTMLElement,
    overlay: HTMLElement | null,
    dropdownContainer: HTMLElement,
    node: HTMLElement,
    fromKeyboard = false
  ) => {
    const trigger = dropdownLink.firstElementChild as HTMLElement;
    lockPageScroll();
    trigger.setAttribute("aria-expanded", "true");
    node.classList.add("is-dropdown-open");
    node.classList.add("dropdown-active");
    overlay?.classList.add("is-active");
    node
      .querySelectorAll<HTMLElement>(".site-nav--has-dropdown.site-nav--dropdown-open")
      .forEach((el) => {
        el.classList.remove("site-nav--dropdown-open");
      });
    dropdownLink.classList.add("site-nav--dropdown-open");
    dropdownContainer
      .querySelectorAll<HTMLElement>(".site-nav-dropdown__menu--active")
      .forEach((el) => el.classList.remove("site-nav-dropdown__menu--active"));

    navDropdown.classList.add("site-nav-dropdown__menu--active");

    const innerParents =
      navDropdown.querySelectorAll<HTMLElement>(".site-nav-dropdown__link-parent");
    if (innerParents.length) {
      activateInnerPanel(innerParents[0], innerParents, navDropdown);
    }

    dropdownContainer.classList.remove("tw-hidden");
    setTimeout(() => {
      dropdownContainer.classList.add("tw-opacity-100");
      initNavCarousels(navDropdown);
      const closeButton = navDropdown.querySelector<HTMLElement>(
        ".site-nav-dropdown-close"
      );
      const firstFocusable = navDropdown.querySelector<HTMLElement>(
        "a, button, input, [tabindex]:not([tabindex='-1'])"
      );

      const isKeyboardNavigation =
        fromKeyboard || trigger === document.activeElement;

      if (isKeyboardNavigation && firstFocusable) {
        lastActiveTrigger = trigger;
        firstFocusable.focus();
      }
      if (closeButton) {
        closeButton.onclick = () => {
          closeDropdown(
            dropdownLink,
            navDropdown,
            overlay,
            dropdownContainer,
            node
          );
        };
      }
    }, 0);
  };

  const closeDropdown = (
    dropdownLink: HTMLElement,
    navDropdown: HTMLElement,
    overlay: HTMLElement | null,
    dropdownContainer: HTMLElement,
    node: HTMLElement,
    options?: { skipTriggerRefocus?: boolean }
  ) => {
    const skipTriggerRefocus = options?.skipTriggerRefocus ?? false;

    const trigger = dropdownLink.firstElementChild as HTMLElement;
    trigger.setAttribute("aria-expanded", "false");
    dropdownLink.classList.remove("site-nav--dropdown-open");
    node.classList.remove("is-dropdown-open");
    node.classList.remove("dropdown-active");
    overlay?.classList.remove("is-active");
    unlockPageScroll();
    navDropdown.classList.remove("site-nav-dropdown__menu--active");
    dropdownContainer.classList.remove("tw-opacity-100");
    setTimeout(() => {
      dropdownContainer.classList.add("tw-hidden");
    }, 0);

    removeTrapFocus();

    if (!skipTriggerRefocus && lastActiveTrigger) {
      lastActiveTrigger.focus();
      lastActiveTrigger = null;
    } else if (skipTriggerRefocus) {
      lastActiveTrigger = null;
    }
  };

  const handleInnerDropdownKeypress = (
    e: KeyboardEvent,
    innerDropdownButtons: NodeListOf<HTMLElement>,
    navDropdown: HTMLElement
  ) => {
    const { code } = e;
    if (code !== "Tab") e.preventDefault();
    if (code === "Space" || code === "Enter") {
      const current = e.currentTarget as HTMLElement;
      activateInnerPanel(current, innerDropdownButtons, navDropdown);
      initNavCarousels(navDropdown);
    }
  };

  const handleKeypress = (
    dropdownLink: HTMLElement,
    e: KeyboardEvent,
    navDropdown: HTMLElement,
    overlay: HTMLElement | null,
    dropdownContainer: HTMLElement,
    node: HTMLElement
  ) => {
    const { code } = e;

    if (code === "Tab") {
      if (!e.shiftKey) {
        e.preventDefault();
        const trigger = dropdownLink.firstElementChild as HTMLElement;
        const isThisMenuActive =
          overlay?.classList.contains("is-active") &&
          navDropdown.classList.contains("site-nav-dropdown__menu--active");

        if (isThisMenuActive) {
          const firstFocusable = navDropdown.querySelector<HTMLElement>(
            "a, button, input, [tabindex]:not([tabindex='-1'])"
          );
          if (firstFocusable) {
            lastActiveTrigger = trigger;
            firstFocusable.focus();
          }
        } else {
          openDropdown(
            dropdownLink,
            navDropdown,
            overlay,
            dropdownContainer,
            node,
            true
          );
        }
      }
      return;
    }

    if (code !== "Tab") e.preventDefault();
    const forceClose = code === "Escape";
    if (code === "Space" || code === "Enter" || forceClose) {
      overlay?.classList.contains("is-active") || forceClose
        ? closeDropdown(
            dropdownLink,
            navDropdown,
            overlay,
            dropdownContainer,
            node
          )
        : openDropdown(
            dropdownLink,
            navDropdown,
            overlay,
            dropdownContainer,
            node
          );
    }
  };

  const initNav = (node: HTMLElement, overlay: HTMLElement | null) => {
    const closeActiveMegaMenu = () => {
      if (!node.classList.contains("dropdown-active")) {
        return;
      }
      const dropdownContainer =
        node.querySelector<HTMLElement>(".site-nav-dropdown");
      if (!dropdownContainer) {
        return;
      }
      const activePanel = dropdownContainer.querySelector<HTMLElement>(
        ".site-nav-dropdown__menu.site-nav-dropdown__menu--active"
      );
      if (!activePanel) {
        return;
      }
      const handle = getChildListHandleFromPanel(activePanel);
      if (!handle) {
        return;
      }
      const dropdownLink = node.querySelector<HTMLElement>(
        `[data-child-list-handle="${handle}"]`
      );
      if (!dropdownLink) {
        return;
      }
      closeDropdown(
        dropdownLink,
        activePanel,
        overlay,
        dropdownContainer,
        node
      );
    };

    overlay?.addEventListener("click", closeActiveMegaMenu);

    node
      .querySelectorAll<HTMLElement>(".site-nav > .site-nav--has-dropdown")
      .forEach((dropdownLink, idx) => {
        const dropdownContainer =
          node.querySelector<HTMLElement>(".site-nav-dropdown");
        const linkName = dropdownLink.getAttribute("data-child-list-handle");
        const navDropdown = node.querySelector<HTMLElement>(
          `.site-nav-dropdown__menu--${linkName}`
        );

        const trigger = dropdownLink.firstElementChild as HTMLElement;

        const handleTriggerClick = (e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          const isThisOpen =
            navDropdown.classList.contains("site-nav-dropdown__menu--active") &&
            Boolean(overlay?.classList.contains("is-active"));
          if (isThisOpen) {
            closeDropdown(
              dropdownLink,
              navDropdown,
              overlay,
              dropdownContainer,
              node
            );
          } else {
            openDropdown(
              dropdownLink,
              navDropdown,
              overlay,
              dropdownContainer,
              node
            );
          }
        };

        trigger.addEventListener("click", handleTriggerClick);

        trigger.addEventListener(
          "keydown",
          (e: KeyboardEvent) =>
            handleKeypress(
              dropdownLink,
              e,
              navDropdown,
              overlay,
              dropdownContainer,
              node
            )
        );

        const handleSubmenuTabKeydown = (e: KeyboardEvent) => {
          if (e.key !== "Tab") return;
          if (!navDropdown.classList.contains("site-nav-dropdown__menu--active")) {
            return;
          }
          if (!overlay?.classList.contains("is-active")) return;

          const focusables = getVisibleFocusables(navDropdown);
          if (!focusables.length) return;

          const active = document.activeElement as HTMLElement | null;
          if (!active) return;
          const activeIndex = focusables.indexOf(active);
          if (activeIndex === -1) return;

          const trigger = dropdownLink.firstElementChild as HTMLElement;

          if (e.shiftKey && activeIndex === 0) {
            e.preventDefault();
            lastActiveTrigger = null;
            closeDropdown(dropdownLink, navDropdown, overlay, dropdownContainer, node, {
              skipTriggerRefocus: true,
            });
            trigger.focus();
            return;
          }

          if (!e.shiftKey && activeIndex === focusables.length - 1) {
            e.preventDefault();
            lastActiveTrigger = null;
            closeDropdown(dropdownLink, navDropdown, overlay, dropdownContainer, node, {
              skipTriggerRefocus: true,
            });
            const navUl = node.querySelector<HTMLElement>("#SiteNav");
            if (navUl) {
              focusNextTopLevelOrOut(node, navUl, trigger);
            }
          }
        };

        navDropdown.addEventListener("keydown", handleSubmenuTabKeydown);

        const innerDropdownButtons =
          navDropdown.querySelectorAll<HTMLElement>(
            ".site-nav-dropdown__link-parent"
          );
        if (innerDropdownButtons.length) {
          innerDropdownButtons.forEach((button, idx) => {
            if (idx === 0) {
              button.classList.add("site-nav-dropdown__child-link--active");
              const firstHandle = button.getAttribute("data-handle");
              const firstMenu = queryPanelByHandle(navDropdown, firstHandle);
              firstMenu?.classList.add("site-nav-dropdown__child-menu--active");
            }

            const onInnerClick = (e: MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              activateInnerPanel(button, innerDropdownButtons, navDropdown);
              initNavCarousels(navDropdown);
            };

            const onInnerKeydown = (e: KeyboardEvent) =>
              handleInnerDropdownKeypress(
                e,
                innerDropdownButtons,
                navDropdown
              );

            button.addEventListener("click", onInnerClick);
            button.addEventListener("keydown", onInnerKeydown);
          });
        }

        return () => {
          trigger.removeEventListener("click", handleTriggerClick);
        };
      });
  };

  initNav(node, overlay);
});
